import { useMemo, useRef } from 'react'
import { WebView, type WebViewMessageEvent } from 'react-native-webview'

/** Where the WebSDK builder is served, and the origin the document runs as. */
const SUMSUB_ORIGIN = 'https://static.sumsub.com'
const BUILDER_SRC = `${SUMSUB_ORIGIN}/idensic/static/sns-websdk-builder.js`

export interface SumsubKycSurfaceProps {
  accessToken: string
  /** Drawn to match the host, rather than the SDK's own light default. */
  scheme: 'light' | 'dark'
  /**
   * Mints a replacement token. The SDK's own expires in minutes — well inside
   * a document capture — and it asks for a new one rather than failing.
   */
  onRenewToken: () => Promise<string | undefined>
  /** The applicant's checks are in; the round can be reported. */
  onSubmitted: () => void
  onFailed: (code: string) => void
}

/**
 * Sumsub's WebSDK, hosted in a WebView.
 *
 * The document is ours and the token is injected into it, so the SDK never
 * arrives over a URL a response chose — `params` carries a credential, not a
 * page to open.
 */
export function SumsubKycSurface({
  accessToken,
  scheme,
  onRenewToken,
  onSubmitted,
  onFailed,
}: SumsubKycSurfaceProps) {
  const webView = useRef<WebView>(null)
  // Held in a ref so a re-render cannot reload the SDK mid-verification: the
  // user would lose a part-finished document capture.
  const callbacks = useRef({ onSubmitted, onFailed, onRenewToken })
  callbacks.current = { onSubmitted, onFailed, onRenewToken }

  const html = useMemo(
    () => buildDocument(accessToken, scheme),
    [accessToken, scheme],
  )

  const handleMessage = (event: WebViewMessageEvent) => {
    // Only our own document may speak. Sumsub's flow navigates subframes to
    // its own hosts, and one of those posting a `submitted` we forwarded
    // would clear a round the applicant never finished.
    if (originOf(event.nativeEvent.url) !== SUMSUB_ORIGIN) {
      return
    }
    const message = parseMessage(event.nativeEvent.data)
    if (message?.type === 'submitted') {
      callbacks.current.onSubmitted()
      return
    }
    if (message?.type === 'renew') {
      // The document cannot mint one: the request is authenticated, and the
      // credential never belongs in a page.
      void callbacks.current.onRenewToken().then((token) => {
        webView.current?.injectJavaScript(
          `window.__funkitRenewed(${JSON.stringify(token ?? '')}); true;`,
        )
      })
      return
    }
    if (message?.type === 'failed') {
      callbacks.current.onFailed(message.code ?? 'sdk_error')
    }
  }

  return (
    <WebView
      ref={webView}
      // `baseUrl` is what gives the document its origin, which the message
      // check above is pinned to.
      source={{ html, baseUrl: `${SUMSUB_ORIGIN}/` }}
      onMessage={handleMessage}
      onError={({ nativeEvent }) =>
        callbacks.current.onFailed(`load_failed_${nativeEvent.code}`)
      }
      originWhitelist={['https://*.sumsub.com']}
      onShouldStartLoadWithRequest={(request) =>
        request.isTopFrame !== true || isSumsubUrl(request.url)
      }
      javaScriptEnabled
      domStorageEnabled
      // Identity capture is a camera flow: without these the SDK loads and
      // then cannot photograph a document.
      allowsInlineMediaPlayback
      mediaPlaybackRequiresUserAction={false}
      mediaCapturePermissionGrantType="grant"
      style={{ flex: 1, backgroundColor: 'transparent' }}
    />
  )
}

/**
 * The SDK's refresh callback asks the app for a token rather than resolving
 * one itself: minting is an authenticated call, and the credential never
 * belongs in a page. An empty answer rejects, which the SDK reports as an
 * error the screen already offers a retry from.
 */
function buildDocument(accessToken: string, scheme: 'light' | 'dark'): string {
  const token = JSON.stringify(accessToken)
  const theme = JSON.stringify(scheme)
  return `<!doctype html>
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>html,body,#sumsub{margin:0;height:100%;background:transparent}</style>
<div id="sumsub"></div>
<script src="${BUILDER_SRC}"></script>
<script>
  var post = function (body) {
    window.ReactNativeWebView.postMessage(JSON.stringify(body))
  }
  // Held so the app's answer can resolve the SDK's own refresh promise.
  var pending = null
  window.__funkitRenewed = function (token) {
    if (!pending) return
    var settle = pending
    pending = null
    token ? settle.resolve(token) : settle.reject(new Error('no token'))
  }
  var hasSubmitted = false
  var submitted = function () {
    if (hasSubmitted) return
    hasSubmitted = true
    post({ type: 'submitted' })
  }
  var renew = function () {
    return new Promise(function (resolve, reject) {
      pending = { resolve: resolve, reject: reject }
      post({ type: 'renew' })
    })
  }
  try {
    window.snsWebSdk
      .init(${token}, renew)
      .withConf({ lang: 'en', theme: ${theme} })
      // The document is ours and sized by the view, so the SDK neither writes
      // a viewport tag nor measures its own frame.
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      .on('idCheck.onReady', function () { post({ type: 'ready' }) })
      // Reported once per mount: three events mean the same thing here, and
      // the second would post under a fresh idempotency key.
      .on('idCheck.onApplicantStatusChanged', function (payload) {
        // Not every status change is a submission — a review that reopens
        // fires this too, and reporting that ends the capture early.
        var status = (payload && payload.reviewStatus) || ''
        if (status === 'pending' || status === 'completed') submitted()
      })
      .on('idCheck.onUploadError', function (error) {
        post({ type: 'failed', code: (error && error.code) || 'upload_error' })
      })
      .on('idCheck.onApplicantSubmitted', function () { submitted() })
      .on('idCheck.onApplicantResubmitted', function () { submitted() })
      .on('idCheck.onError', function (error) {
        post({ type: 'failed', code: (error && error.code) || 'sdk_error' })
      })
      .build()
      .launch('#sumsub')
  } catch (caught) {
    post({ type: 'failed', code: 'sdk_init_failed' })
  }
</script>`
}

interface SdkMessage {
  type: string
  code?: string
}

function parseMessage(data: string): SdkMessage | undefined {
  try {
    const parsed: unknown = JSON.parse(data)
    if (parsed === null || typeof parsed !== 'object') {
      return undefined
    }
    const { type, code } = parsed as Record<string, unknown>
    if (typeof type !== 'string') {
      return undefined
    }
    return { type, ...(typeof code === 'string' ? { code } : {}) }
  } catch {
    return undefined
  }
}

function originOf(url: string): string | undefined {
  const match = /^(https:\/\/[^/?#]+)/.exec(url)
  return match?.[1]
}

function isSumsubUrl(url: string): boolean {
  const origin = originOf(url)
  if (origin === undefined) {
    return false
  }
  const host = origin.slice('https://'.length)
  return host === 'sumsub.com' || host.endsWith('.sumsub.com')
}
